export type Locale = 'ko' | 'en';

/**
 * Language is carried by the URL rather than by browser detection: Googlebot renders with an
 * en-US locale, so detecting on the client would hand the crawler the English page and drop the
 * Korean content — which is what lol.ysw.kr actually ranks on — out of the index. `/` stays
 * Korean, `/en/` serves English, and the two are linked with hreflang.
 *
 * Locale never changes within a page lifetime (the switch is a navigation), so callers can read
 * this once at module scope.
 */
export const detectLocale = (): Locale => (/^\/en(\/|$)/.test(window.location.pathname) ? 'en' : 'ko');

export const pathForLocale = (locale: Locale): string => (locale === 'en' ? '/en/' : '/');

export const DDRAGON_LOCALE: Record<Locale, string> = {
  ko: 'ko_KR',
  en: 'en_US',
};

interface ParsedStat {
  /** The stat as Data Dragon literally writes it in that locale's item description. */
  label: Record<Locale, string>;
  /** Canonical stat id when the number carries no percent sign. */
  flat: string;
  /** Canonical stat id when the number carries a percent sign, where the game splits the two. */
  percent?: string;
}

/**
 * Two English labels deliberately differ from their canonical id, because Data Dragon writes
 * "Move Speed" and "Heal and Shield Power" while the Jack of All Trades list has always called
 * them "Movement Speed" and "Health and Shield Power". These were read off the live data, not
 * guessed — assuming the ids matched would have silently dropped both stats.
 */
export const PARSED_STATS: ParsedStat[] = [
  { label: { ko: '체력', en: 'Health' }, flat: 'Health' },
  { label: { ko: '기본 체력 재생', en: 'Base Health Regen' }, flat: 'Base Health Regen' },
  { label: { ko: '방어력', en: 'Armor' }, flat: 'Armor' },
  { label: { ko: '마법 저항력', en: 'Magic Resist' }, flat: 'Magic Resist' },
  { label: { ko: '강인함', en: 'Tenacity' }, flat: 'Tenacity' },
  { label: { ko: '공격력', en: 'Attack Damage' }, flat: 'Attack Damage' },
  { label: { ko: '공격 속도', en: 'Attack Speed' }, flat: 'Attack Speed' },
  { label: { ko: '치명타 확률', en: 'Critical Strike Chance' }, flat: 'Critical Strike Chance' },
  { label: { ko: '물리 관통력', en: 'Lethality' }, flat: 'Lethality' },
  { label: { ko: '방어구 관통력', en: 'Armor Penetration' }, flat: 'Armor Penetration' },
  { label: { ko: '생명력 흡수', en: 'Life Steal' }, flat: 'Life Steal' },
  { label: { ko: '스킬 가속', en: 'Ability Haste' }, flat: 'Ability Haste' },
  { label: { ko: '마나', en: 'Mana' }, flat: 'Mana' },
  { label: { ko: '기본 마나 재생', en: 'Base Mana Regen' }, flat: 'Base Mana Regen' },
  { label: { ko: '주문력', en: 'Ability Power' }, flat: 'Ability Power' },
  {
    label: { ko: '마법 관통력', en: 'Magic Penetration' },
    flat: 'Magic Penetration',
    percent: 'Percent Magic Penetration',
  },
  {
    label: { ko: '이동 속도', en: 'Move Speed' },
    flat: 'Movement Speed',
    percent: 'Percent Movement Speed',
  },
  { label: { ko: '체력 회복 및 보호막', en: 'Heal and Shield Power' }, flat: 'Health and Shield Power' },
];

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Korean writes the stat first ("공격력 36", "공격 속도 30%"); English writes the number first
 * ("36 Attack Damage", "30% Attack Speed"). Labels are ordered longest-first so that, say,
 * "체력 회복 및 보호막" is never consumed as a bare "체력".
 */
export const buildStatPattern = (locale: Locale): RegExp => {
  const labels = PARSED_STATS.map((stat) => stat.label[locale])
    .sort((a, b) => b.length - a.length)
    .map(escapeForRegExp)
    .join('|');

  return locale === 'ko'
    ? new RegExp(`^(${labels})\\s+(\\d+)(%?)`)
    : new RegExp(`^(\\d+)(%?)\\s+(${labels})`);
};

export const statByLabel = (locale: Locale, label: string): ParsedStat | undefined =>
  PARSED_STATS.find((stat) => stat.label[locale] === label);

/**
 * 세계 지도집 / World Atlas (3865) ships an empty description, so its stats are spelled out here.
 * The (%) suffixes match what the app has always produced for this item.
 */
export const ATLAS_FALLBACK_STATS: Record<Locale, { [key: string]: { value: number } }> = {
  ko: {
    체력: { value: 30 },
    '기본 체력 재생(%)': { value: 25 },
    '기본 마나 재생(%)': { value: 25 },
    '10초당 골드': { value: 3 },
  },
  en: {
    Health: { value: 30 },
    'Base Health Regen(%)': { value: 25 },
    'Base Mana Regen(%)': { value: 25 },
    'Gold per 10s': { value: 3 },
  },
};

export const ATLAS_FALLBACK_DESCRIPTION: Record<Locale, string> = {
  ko: `
        체력 <b>30</b><br>
        기본 체력 재생 <b>25%</b><br>
        기본 마나 재생 <b>25%</b><br>
        10초당 골드 <b>3</b>
      `,
  en: `
        <b>30</b> Health<br>
        <b>25%</b> Base Health Regen<br>
        <b>25%</b> Base Mana Regen<br>
        <b>3</b> Gold per 10 seconds
      `,
};

/** Item tags are locale-independent in Data Dragon, so only the display label changes. */
export const CATEGORY_LABELS: Record<Locale, { [key: string]: string }> = {
  ko: {
    Lane: '라인전',
    Health: '체력',
    Damage: '데미지',
    AttackSpeed: '공격 속도',
    ArmorPenetration: '방어구 관통력',
    Lifesteal: '생명력 흡수',
    SpellDamage: '주문 피해',
    SpellVamp: '주문 흡혈',
    CooldownReduction: '재사용 대기시간 감소',
    AbilityHaste: '스킬 가속',
    SpellBlock: '주문 방어',
    Armor: '방어력',
    Slow: '둔화',
    HealthRegen: '체력 재생',
    Mana: '마나',
    ManaRegen: '마나 재생',
    Vision: '시야',
    Boots: '신발',
    NonbootsMovement: '이동 속도',
    Active: '액티브',
    OnHit: '적중 시 효과',
    GoldPer: '골드 획득',
  },
  en: {
    Lane: 'Lane',
    Health: 'Health',
    Damage: 'Damage',
    AttackSpeed: 'Attack Speed',
    ArmorPenetration: 'Armor Penetration',
    Lifesteal: 'Life Steal',
    SpellDamage: 'Ability Power',
    SpellVamp: 'Spell Vamp',
    CooldownReduction: 'Cooldown Reduction',
    AbilityHaste: 'Ability Haste',
    SpellBlock: 'Magic Resist',
    Armor: 'Armor',
    Slow: 'Slow',
    HealthRegen: 'Health Regen',
    Mana: 'Mana',
    ManaRegen: 'Mana Regen',
    Vision: 'Vision',
    Boots: 'Boots',
    NonbootsMovement: 'Move Speed',
    Active: 'Active',
    OnHit: 'On-Hit',
    GoldPer: 'Gold Income',
  },
};

/**
 * English display names for the canonical stat ids. The ids themselves stay put — they key the
 * Jack of All Trades list and the parsed stat totals — but a few of them read badly on screen:
 * Riot says "Move Speed" and "Heal and Shield Power", and "Percent Magic Penetration" is not a
 * phrase anyone uses. Ids without an entry here display as-is.
 */
export const STAT_DISPLAY_EN: { [id: string]: string } = {
  'Magic Penetration': 'Magic Penetration (flat)',
  'Percent Magic Penetration': 'Magic Penetration (%)',
  'Movement Speed': 'Move Speed (flat)',
  'Percent Movement Speed': 'Move Speed (%)',
  'Health and Shield Power': 'Heal and Shield Power',
};

/**
 * Nicknames Korean players use that Riot's colloq field misses. Keyed by item id, not by name:
 * Riot renames items (서리불꽃 건틀릿 became 얼어붙은 건틀릿) and the app browses old patches, so a
 * name-keyed table would rot. Entries for items no longer sold stay put — they light up again on
 * the patches where the item existed.
 */
export const EXTRA_NICKNAMES: { [itemId: string]: string[] } = {
  // 장화
  '1001': ['똥신'],
  '3006': ['광전사', '공속신'],
  '3009': ['신속신'],
  '3020': ['마관신'],
  '3047': ['닌탑'],
  '3117': ['모빌'],
  '3158': ['쿨감신'],
  // 시작 아이템
  '1004': ['요부'],
  '1006': ['완두콩', '부랄'],
  '1035': ['빨간강타'],
  '1039': ['파란강타'],
  '1054': ['도방'],
  '1055': ['도란검'],
  '1056': ['도란링'],
  '3070': ['여눈'],
  '3850': ['주도검'],
  // 하위 아이템
  '1038': ['비에프'],
  '1053': ['흡낫'],
  '1057': ['조개'],
  '1058': ['쓸큰지'],
  '2020': ['야몽', '브루탈'],
  '2420': ['추적자', '팔목보호대', '추팔'],
  '2523': ['씨포포', '씨뽀리포'],
  '3035': ['라위'],
  '3044': ['탐식', '탐망', '페이지'],
  '3051': ['온필자', '온필도', '온도끼'],
  '3057': ['광휘', '쉰'],
  '3086': ['질', 'zeal'],
  '3123': ['치감칼'],
  '3140': ['퀵실'],
  '3145': ['교발', '발전기'],
  '3155': ['주포', '헥드', '헥스'],
  '3916': ['망각구', '망구', '치감구'],
  '6670': ['화살통'],
  '6676': ['징총'],
  // 코어 아이템
  '3026': ['가엔'],
  '3031': ['인피', '무대'],
  '3032': ['윤탈'],
  '3033': ['모탈'],
  '3036': ['거학'],
  '3046': ['팬댄'],
  '3065': ['비사지'],
  '3068': ['썬파', '태불방'],
  '3071': ['블클'],
  '3072': ['블써'],
  '3073': ['헥스'],
  '3074': ['굶드라'],
  '3075': ['가갑'],
  '3078': ['트포'],
  '3089': ['라바돈', '데캡'],
  '3091': ['마최', '위치엔드'],
  '3094': ['고연포'],
  '3095': ['폭갈'],
  '3109': ['기맹'],
  '3110': ['얼심'],
  '3119': ['종말', '겨울'],
  '3135': ['보이드'],
  '3153': ['몰왕', '몰왕검'],
  '3179': ['그검'],
  '3181': ['선파자'],
  '3190': ['솔라리'],
  '3742': ['망갑'],
  '3748': ['거드라'],
  '3814': ['밤끝'],
  '4636': ['밤수'],
  '4644': ['부여왕'],
  '4645': ['그불'],
  '6333': ['죽무'],
  '6610': ['갈하'],
  '6616': ['흐물지'],
  '6630': ['선포'],
  '6631': ['발분'],
  '6632': ['신파', '신파자'],
  '6662': ['얼건'],
  '6693': ['자발'],
  '6696': ['원형낫'],
  '6699': ['벼락', '벼폭검'],
  '8020': ['심가'],
};

/**
 * Korean players shorten an item name to the first syllable of each word — 무한의 대검 becomes 무대,
 * 얼어붙은 심장 becomes 얼심. Riot registered only seven of these in colloq (요부, 천갑, 흡낫, 무대,
 * 탐망, 얼심, 몰왕검), which is what tells us the rule matches real usage rather than inventing it.
 */
export const koreanAbbreviation = (name: string): string | null => {
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length < 2) return null;

  const abbreviation = words.map((word) => word[0]).join('');
  return /^[가-힣]+$/.test(abbreviation) ? abbreviation : null;
};

export interface SearchEntry {
  /** Item names, matched on substrings. */
  names: string[];
  /** Nicknames and abbreviations, matched more strictly — see matchesQuery. */
  aliases: string[];
}

const splitColloq = (colloq?: string): string[] =>
  (colloq ?? '').split(';').map((term) => term.trim()).filter(Boolean);

/**
 * Both locales feed every entry. Riot's Korean colloq carries roughly six times the nicknames of
 * the English one (401 terms against 63), so an English-only index would leave /en/ unable to find
 * 무한의 대검 by "infinity edge" — Korean data has both words, English data has only "ie".
 */
export const buildSearchEntry = (
  id: string,
  names: (string | undefined)[],
  colloqs: (string | undefined)[]
): SearchEntry => {
  const aliases = new Set<string>();

  colloqs.forEach((colloq) => splitColloq(colloq).forEach((term) => aliases.add(term.toLowerCase())));
  names.forEach((name) => {
    const abbreviation = name && koreanAbbreviation(name);
    if (abbreviation) aliases.add(abbreviation.toLowerCase());
  });
  (EXTRA_NICKNAMES[id] ?? []).forEach((nickname) => aliases.add(nickname.toLowerCase()));

  return {
    names: names.filter((name): name is string => Boolean(name)).map((name) => name.toLowerCase()),
    aliases: Array.from(aliases),
  };
};

/**
 * Tokens are ANDed, so "무한 대검" and "infinity edge" both land even though neither is a substring
 * of anything.
 *
 * Tokens shorter than three characters are almost always abbreviations, and matching those against
 * arbitrary substrings is useless: "ie" alone pulls in Faer(ie) Charm, Doran's Sh(ie)ld, F(ie)ndish
 * Codex and Caulf(ie)ld's Warhammer. So short tokens have to start a word, or hit an alias exactly.
 * That still leaves two-syllable Korean queries like 장화 or 대검 working, since those do start a
 * word, while "ie" now resolves to the one item that actually claims it.
 */
export const matchesQuery = (entry: SearchEntry, query: string): boolean => {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;

  return tokens.every((token) => {
    if (token.length >= 3) {
      return (
        entry.names.some((name) => name.includes(token)) ||
        entry.aliases.some((alias) => alias.includes(token))
      );
    }

    return (
      entry.names.some((name) => name.split(/\s+/).some((word) => word.startsWith(token))) ||
      entry.aliases.some((alias) => alias === token)
    );
  });
};

interface UiStrings {
  titleSub: string;
  languageSwitchLabel: string;
  reset: string;
  totalGoldLabel: string;
  totalStats: string;
  emptySlots: string;
  jackOfAllTrades: string;
  stacks: (count: number) => string;
  currentStacks: string;
  abilityHaste: string;
  adaptiveBonus: string;
  jackDescription: string;
  activeStats: string;
  inactiveStats: string;
  allItems: string;
  searchPlaceholder: string;
  categoryFilter: string;
  categoryFilterSuffix: string;
  jackFilter: string;
  jackFilterSuffix: string;
  loading: string;
  cost: string;
  goldUnit: string;
  effects: string;
  buildsInto: string;
  buildsFrom: string;
  none: string;
  close: string;
  buy: string;
  sell: string;
  inventoryFull: string;
  disclaimer: string[];
}

export const UI: Record<Locale, UiStrings> = {
  ko: {
    titleSub: '다재다능 시뮬레이터',
    languageSwitchLabel: 'English',
    reset: '초기화',
    totalGoldLabel: '총 골드: ',
    totalStats: '능력치 총합',
    emptySlots: '아이템 창이 비어 있습니다. 오른쪽에서 아이템을 선택하여 추가하세요.',
    jackOfAllTrades: '다재다능',
    stacks: (count) => `${count}중첩`,
    currentStacks: '현재 중첩: ',
    abilityHaste: '스킬 가속: ',
    adaptiveBonus: '추가 적응형 능력치: ',
    jackDescription:
      '다재다능에서 얻는 능력치: 아이템으로 얻은 서로 다른 능력치 하나당 잭 중첩을 얻습니다. 중첩 하나당 스킬 가속이 1 증가합니다. 5회 및 10회 중첩시 각각 10 또는 25의 추가 적응형 능력치를 획득했습니다.',
    activeStats: '활성화 능력:',
    inactiveStats: '비활성화 능력치:',
    allItems: '전체 아이템',
    searchPlaceholder: '아이템 이름이나 다른 이름(예: 똥신)으로 검색',
    categoryFilter: '카테고리',
    categoryFilterSuffix: ' 필터:',
    jackFilter: '다재다능',
    jackFilterSuffix: ' 필터:',
    loading: '아이템을 불러오는 중...',
    cost: '가격:',
    goldUnit: '골드',
    effects: '효과',
    buildsInto: '상위 아이템:',
    buildsFrom: '하위 아이템:',
    none: '없음',
    close: '닫기',
    buy: '구매',
    sell: '판매',
    inventoryFull: '인벤토리가 가득 찼습니다',
    disclaimer: [
      'EN: Item Builder(Jack of All Trades Simulator) is not endorsed by Riot Games and does not reflect the views or opinions of Riot Games or anyone officially involved in producing or managing Riot Games properties. Riot Games and all associated properties are trademarks or registered trademarks of Riot Games, Inc.',
      'KR: 롤 아이템 빌더(다재다능 시뮬레이터)는 Riot Games와 연관이 없으며, Riot Games 또는 라이엇 게임즈 자산을 생산 및 관리하는 그 어떤 곳과도 공식적인 관계가 없습니다. Riot Games 및 모든 관련 자산은 Riot Games, Inc.의 상표 또는 등록 상표입니다.',
    ],
  },
  en: {
    titleSub: 'Jack of All Trades Simulator',
    languageSwitchLabel: '한국어',
    reset: 'Reset',
    totalGoldLabel: 'Total gold: ',
    totalStats: 'Total stats',
    emptySlots: 'No items yet. Pick items from the list to fill your build.',
    jackOfAllTrades: 'Jack of All Trades',
    stacks: (count) => `${count} stacks`,
    currentStacks: 'Stacks: ',
    abilityHaste: 'Ability Haste: ',
    adaptiveBonus: 'Adaptive Force: ',
    jackDescription:
      'Jack of All Trades grants one stack per distinct stat your items give you. Each stack grants 1 Ability Haste, and at 5 and 10 stacks you also gain 10 or 25 Adaptive Force.',
    activeStats: 'Active stats',
    inactiveStats: 'Inactive stats',
    allItems: 'All items',
    searchPlaceholder: 'Search by item name',
    categoryFilter: 'Category',
    categoryFilterSuffix: ' filter',
    jackFilter: 'Jack stat',
    jackFilterSuffix: ' filter',
    loading: 'Loading items...',
    cost: 'Cost:',
    goldUnit: 'gold',
    effects: 'Effects',
    buildsInto: 'Builds into:',
    buildsFrom: 'Builds from:',
    none: 'None',
    close: 'Close',
    buy: 'Buy',
    sell: 'Sell',
    inventoryFull: 'Inventory is full',
    disclaimer: [
      'Item Builder (Jack of All Trades Simulator) is not endorsed by Riot Games and does not reflect the views or opinions of Riot Games or anyone officially involved in producing or managing Riot Games properties. Riot Games and all associated properties are trademarks or registered trademarks of Riot Games, Inc.',
    ],
  },
};
